import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Star, ShieldCheck, CheckCircle, ThumbsUp, ThumbsDown,
    MessageSquare, AlertCircle, Briefcase, FileText, Phone
} from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { HomlyLogo } from '../../components/brand/HomlyLogo';
import { Badge } from '../../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const REVIEW_TYPE_CONFIG = {
    contact: {
        label: 'Avis de contact',
        description: 'Comment s\'est passée la prise de contact ?',
        icon: Phone,
        color: '#3B82F6',
        subRatings: [
            { key: 'reactivity', label: 'Réactivité' },
            { key: 'clarity', label: 'Clarté des échanges' },
            { key: 'professionalism', label: 'Professionnalisme' },
        ]
    },
    quote: {
        label: 'Avis de devis',
        description: 'Comment évaluez-vous le devis reçu ?',
        icon: FileText,
        color: '#F59E0B',
        subRatings: [
            { key: 'reactivity', label: 'Réactivité' },
            { key: 'quote_clarity', label: 'Clarté du devis' },
            { key: 'price_fairness', label: 'Rapport qualité/prix' },
        ]
    },
    job: {
        label: 'Avis de chantier',
        description: 'Comment s\'est déroulé le chantier ?',
        icon: Briefcase,
        color: '#22C55E',
        subRatings: [
            { key: 'quality', label: 'Qualité du travail' },
            { key: 'deadlines', label: 'Respect des délais' },
            { key: 'budget_respect', label: 'Respect du budget' },
            { key: 'cleanliness', label: 'Propreté du chantier' },
            { key: 'professionalism', label: 'Professionnalisme' },
        ]
    },
};

function InteractiveStars({ value, onChange, size = 28 }) {
    const [hover, setHover] = useState(0);
    return (
        <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map(i => (
                <Star
                    key={i}
                    size={size}
                    className={`cursor-pointer transition-all ${
                        i <= (hover || value) ? 'text-yellow-400 fill-yellow-400 scale-110' : 'text-slate-400'
                    }`}
                    onMouseEnter={() => setHover(i)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => onChange(i)}
                />
            ))}
            {value > 0 && <span className="ml-2 text-sm font-semibold text-foreground">{value}/5</span>}
        </div>
    );
}

export default function ProReviewPage() {
    const { token } = useParams();
    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitted, setSubmitted] = useState(false);

    // Form state
    const [reviewType, setReviewType] = useState('');
    const [overallRating, setOverallRating] = useState(0);
    const [subRatings, setSubRatings] = useState({});
    const [comment, setComment] = useState('');
    const [recommends, setRecommends] = useState(true);
    const [reviewerName, setReviewerName] = useState('');
    const [city, setCity] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        async function fetchFormData() {
            try {
                const res = await fetch(`${API}/api/professionals/v2/reviews/form/${token}`);
                if (res.ok) {
                    const data = await res.json();
                    setFormData(data);
                    setReviewerName(data.client_name || '');
                    // Default to the best available type
                    const types = data.available_review_types || ['contact'];
                    setReviewType(types[types.length - 1]);
                } else {
                    const err = await res.json();
                    setError(err.detail || 'Lien invalide');
                }
            } catch {
                setError('Erreur de connexion');
            }
            setLoading(false);
        }
        fetchFormData();
    }, [token]);

    const submit = async () => {
        if (overallRating === 0) return;
        setSubmitting(true);
        try {
            const body = {
                review_type: reviewType,
                overall_rating: overallRating,
                ...subRatings,
                comment,
                recommends,
                reviewer_name: reviewerName,
                city,
            };
            const res = await fetch(`${API}/api/professionals/v2/reviews/submit/${token}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (res.ok) {
                setSubmitted(true);
            } else {
                const err = await res.json();
                setError(err.detail || 'Erreur lors de la soumission');
            }
        } catch {
            setError('Erreur de connexion');
        }
        setSubmitting(false);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-[#7C3AED] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center px-4">
                <Card className="max-w-md w-full p-8 text-center bg-card border-border">
                    <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                    <h2 className="text-lg font-bold text-foreground mb-2">Lien invalide</h2>
                    <p className="text-sm text-muted-foreground">{error}</p>
                    <Link to="/" className="mt-4 inline-block">
                        <Button variant="outline" size="sm">Retour</Button>
                    </Link>
                </Card>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center px-4">
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
                    <Card className="max-w-md w-full p-8 text-center bg-card border-border" data-testid="review-success">
                        <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                            <CheckCircle className="w-8 h-8 text-green-400" />
                        </div>
                        <h2 className="text-xl font-bold text-foreground mb-2">Merci pour votre avis !</h2>
                        <p className="text-sm text-muted-foreground mb-1">
                            Votre retour d'expérience a été enregistré et sera visible sur le profil de
                        </p>
                        <p className="text-sm font-semibold text-foreground mb-4">{formData?.professional?.company_name}</p>
                        <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-xs">
                            <ShieldCheck className="w-3 h-3 mr-1" /> Avis vérifié Homly
                        </Badge>
                    </Card>
                </motion.div>
            </div>
        );
    }

    const typeCfg = REVIEW_TYPE_CONFIG[reviewType];

    return (
        <div className="min-h-screen bg-background" data-testid="review-form-page">
            {/* Header */}
            <header className="border-b border-border bg-card/80 backdrop-blur-xl sticky top-0 z-50">
                <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-center gap-2">
                    <HomlyLogo size="sm" />
                    <Badge className="bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] text-white border-0 text-[10px]">
                        AVIS VÉRIFIÉ
                    </Badge>
                </div>
            </header>

            <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
                {/* Pro info */}
                <Card className="bg-card border-border p-5">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#F59E0B] flex items-center justify-center shrink-0">
                            <span className="text-white font-bold text-lg">{formData?.professional?.company_name?.charAt(0)}</span>
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-foreground">{formData?.professional?.company_name}</h2>
                            <p className="text-sm text-muted-foreground">{formData?.professional?.specialty}</p>
                        </div>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-[#7C3AED]" />
                        <span className="text-xs text-muted-foreground">
                            Cet avis est lié à une interaction vérifiée sur Homly
                        </span>
                    </div>
                </Card>

                {/* Review type selector */}
                <div>
                    <label className="text-sm font-medium text-foreground mb-3 block">Type d'avis</label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {(formData?.available_review_types || []).map(type => {
                            const cfg = REVIEW_TYPE_CONFIG[type];
                            const TypeIcon = cfg.icon;
                            const isSelected = reviewType === type;
                            return (
                                <button
                                    key={type}
                                    onClick={() => { setReviewType(type); setSubRatings({}); }}
                                    className={`p-3 rounded-xl border text-left transition-all ${
                                        isSelected
                                            ? 'border-2 bg-card'
                                            : 'border-border bg-card hover:border-muted-foreground/30'
                                    }`}
                                    style={isSelected ? { borderColor: cfg.color } : {}}
                                    data-testid={`review-type-${type}`}
                                >
                                    <TypeIcon className="w-5 h-5 mb-1" style={{ color: cfg.color }} />
                                    <p className="text-sm font-medium text-foreground">{cfg.label}</p>
                                    <p className="text-[10px] text-muted-foreground">{cfg.description}</p>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Your name */}
                <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">Votre nom</label>
                    <Input
                        value={reviewerName}
                        onChange={e => setReviewerName(e.target.value)}
                        placeholder="Prénom et nom"
                        data-testid="reviewer-name"
                    />
                </div>

                {/* Overall rating */}
                <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">Note globale</label>
                    <InteractiveStars value={overallRating} onChange={setOverallRating} />
                </div>

                {/* Sub ratings */}
                {typeCfg && (
                    <Card className="bg-card border-border p-4 space-y-4">
                        <h3 className="text-sm font-medium text-foreground">Détails</h3>
                        {typeCfg.subRatings.map(sub => (
                            <div key={sub.key} className="flex items-center justify-between gap-4">
                                <span className="text-sm text-muted-foreground">{sub.label}</span>
                                <InteractiveStars
                                    value={subRatings[sub.key] || 0}
                                    onChange={v => setSubRatings(p => ({ ...p, [sub.key]: v }))}
                                    size={20}
                                />
                            </div>
                        ))}
                    </Card>
                )}

                {/* Comment */}
                <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">Commentaire</label>
                    <textarea
                        value={comment}
                        onChange={e => setComment(e.target.value)}
                        className="w-full bg-card border border-border rounded-lg px-3 py-3 text-sm text-foreground min-h-[100px] focus:ring-1 focus:ring-[#7C3AED] focus:border-[#7C3AED] outline-none"
                        placeholder="Décrivez votre expérience..."
                        data-testid="review-comment"
                    />
                </div>

                {/* City */}
                <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">Ville du projet</label>
                    <Input
                        value={city}
                        onChange={e => setCity(e.target.value)}
                        placeholder="ex: Paris, Lyon..."
                        data-testid="review-city"
                    />
                </div>

                {/* Recommends */}
                <div>
                    <label className="text-sm font-medium text-foreground mb-3 block">Recommanderiez-vous ce professionnel ?</label>
                    <div className="flex gap-3">
                        <button
                            onClick={() => setRecommends(true)}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border transition-all ${
                                recommends ? 'bg-green-500/10 border-green-500/30 text-green-400' : 'bg-card border-border text-muted-foreground'
                            }`}
                            data-testid="recommend-yes"
                        >
                            <ThumbsUp className="w-5 h-5" /> Oui
                        </button>
                        <button
                            onClick={() => setRecommends(false)}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border transition-all ${
                                !recommends ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-card border-border text-muted-foreground'
                            }`}
                            data-testid="recommend-no"
                        >
                            <ThumbsDown className="w-5 h-5" /> Non
                        </button>
                    </div>
                </div>

                {/* Submit */}
                <Button
                    className="w-full bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] text-white h-12 text-base font-semibold"
                    onClick={submit}
                    disabled={overallRating === 0 || submitting}
                    data-testid="submit-review-btn"
                >
                    {submitting ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                        <>
                            <ShieldCheck className="w-5 h-5 mr-2" />
                            Publier mon avis vérifié
                        </>
                    )}
                </Button>

                {/* Trust badge */}
                <p className="text-center text-[10px] text-muted-foreground/60">
                    Cet avis sera publié avec le badge "Vérifié Homly" car il est lié à une interaction réelle tracée sur la plateforme.
                </p>
            </div>
        </div>
    );
}
